import clsx from "clsx";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Container, FormControlLabel, Grid, IconButton, Switch, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
/** - icons */
import LaunchIcon from "@mui/icons-material/Launch";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import * as DBCommonStore from "../../../_appStore/DB_Common";
import * as DatesStore from "../../../_appStore/DB_Dates";
import * as ChartsFiltersStore from "../../../_appStore/DB_Filters_Charts";
import * as TableFiltersStore from "../../../_appStore/DB_Filters_Table";
import * as IndicatorsStore from "../../../_appStore/DB_Indicators";
import * as ChartsBarsSettingsStore from "../../../_appStore/DB_Settings_Chart_Bars";

import { getSelectedTerritorialUnitsItems } from "../../../_appStore/DB_JTs";
import { getSelectedJT, getSelectedSC, getSelectedWSK } from "../../../_appStore/DB_Lists_Table";
import { getLayout, getOptions } from "../../../_appStore/DB_Settings_Table";

/** API */
import {
    getDefaultMetaTableFromAPI,
    getDimensionsAndPositions,
    getTypeOfInformationFromAPI,
    getVariableSectionsFromAPI,
} from "../../../_APIs";

/** Helpers & utils */
import useHelper from "@/src/_common/useHelper";
import uniqBy from "lodash/uniqBy";

/** UI */
import SectionsSkeleton from "../skeletons/SectionsSkeleton";
import ListSkeleton from "../skeletons/ListSkeleton";
import DimensionsSkeleton from "../skeletons/DimensionsSkeleton";
import TreeSkeleton from "../skeletons/TreeSkeleton";
// import SectionsSelect from "./Sections/SectionsSelect";
import AppTooltip from "@/src/_common/AppTooltip";

const FiltersAccordion = dynamic(() => import("./Accordion/_Accordion"));
const SectionsSelect = dynamic(() => import("./Sections/SectionsSelect"), { loading: () => <SectionsSkeleton /> });
const InformationType = dynamic(() => import("./Types/_InformationTypes"), { loading: () => <ListSkeleton /> });
const Dimensions = dynamic(() => import("./Dimensions/_Dimensions"), { loading: () => <DimensionsSkeleton /> });
const Positions = dynamic(() => import("./Positions/_Positions"), { loading: () => <TreeSkeleton /> });
const DimensionsZoomDialog = dynamic(() => import("./Dialogs/DimensionsZoomDialog"));
const TypesZoomDialog = dynamic(() => import("./Dialogs/TypesZoomDialog"));

const useStyles = makeStyles((theme) => ({
    gridItemVertCntr: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
            justifyContent: "flex-end",
        },
        [theme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
            justifyContent: "center",
        },
    },
    gridContainerSections: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(2),
        },
    },
    gridItemSections: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: {
            // maxWidth: "calc(100% - 190px)",
            maxWidth: "calc(100% - 64px)",
        },
    },
    gridItemPositions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(1),
            borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "#fff",
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: "transparent",
            border: "none",
            maxWidth: "calc(100% - 64px)",
        },
    },
    roundBorders: {
        borderRadius: theme.spacing(0.5),
        border: `1px solid #CCC`,
    },
    darkTitle: {
        fontSize: "1rem",
        color: theme.palette.primary.dark,
    },
    dimTitle: {
        fontSize: "1rem",
        color: theme.palette.primary.dark,
        [theme.breakpoints.down("md")]: {
            margin: theme.spacing(0, 0, 1, 0),
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(2, 0, 1, 0),
        },
    },
    posTitle: {
        fontSize: "1rem",
        color: theme.palette.primary.dark,
        [theme.breakpoints.down("md")]: {
            margin: theme.spacing(0, 0, 1, 0),
            fontSize: "0.88888889rem",
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(2, 0, 1, 0),
            fontSize: "1rem",
        },
    },
    fclRoot: {
        marginRight: 0,
        marginLeft: theme.spacing(5),
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            marginTop: theme.spacing(2),
        },
        [theme.breakpoints.down("sm")]: {
            marginLeft: 0,
            marginTop: theme.spacing(1),
        },
    },
    multiLabel: {
        fontSize: "1rem",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.75rem",
        },
    },
    debugger: {
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        marginBottom: 40,
        justifyContent: "space-between",
        "& pre": {
            border: "1px solid #CCC",
            fontSize: "0.5rem",
            maxHeight: 200,
            overflow: "auto",
            margin: "0.5%",
            width: "49%",
            padding: 8,
            backgroundColor: "#EFEFEF",
        },
    },
    hiddenOverlay: {
        position: "absolute",
        top: 1,
        right: 1,
        bottom: 1,
        left: 1,
        borderRadius: 20,
        zIndex: 13,
    },
    disabledColors: {
        "& *": {
            color: "#DDD!important",
        },
    },
    zoomBtn: {
        position: "absolute",
        right: 1,
        top: theme.spacing(2),
        zIndex: 1,
        background: "#fff",
        padding: theme.spacing(0.75),
        "&:after": {
            content: '" "',
            position: "absolute",
            top: -1,
            right: -1,
            bottom: "50%",
            left: -1,
            background: "transparent",
            borderRadius: "39px 39px 0 0",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderBottom: "1px solid rgba(0, 0, 0, 0)",

            zIndex: -1,
        },
        "&:hover": {
            background: "transparent",
            backgroundColor: "#efefef",
        },
        [theme.breakpoints.down("sm")]: {
            top: -4,
            display: "none",
        },
    },
    zoomPosBtn: {
        top: -11,
    },
    preDebug: {
        fontSize: 9,
        maxHeight: 100,
        width: 300,
        overflow: "auto",
        display: "inline-block",
        border: "1px solid #00000011",
        borderRadius: 4,
        marginRight: 2,
        padding: theme.spacing(0.25, 0.5),
    },
}));

const Filters = (props) => {
    const {
        onLoadingDefaults,
        onIsToBeApplied,
        onPredictionData,
        onIsDefaultsError,
        onSelectPositions,
        apply,
        onApply,
    } = props;
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { translationFromJson, getAllIDs } = useHelper();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
    // const isTablet = useMediaQuery((theme) => theme.breakpoints.down("lg"));
    const isTablet = useMediaQuery("(max-width: 1365px)");

    /** Set STORE (table or charts) */
    const tableOrChart = useSelector(DBCommonStore.getTableOrChart);
    const FiltersStore = !tableOrChart || tableOrChart === "table" ? TableFiltersStore : ChartsFiltersStore;
    /** /end Set STORE */

    /** Redux states */
    const currentIndicator = useSelector(IndicatorsStore.getCurrentIndicator);
    const currentSection = useSelector(FiltersStore.getCurrentSection);
    const customerSection = useSelector(DBCommonStore.getCustomerSection);
    const customerDimension = useSelector(DBCommonStore.getCustomerDimension);
    const customerPosition = useSelector(DBCommonStore.getCustomerPosition);
    const defaults = useSelector(FiltersStore.getDefaults);
    const dimensions = useSelector(FiltersStore.getDimensions);
    const layout = useSelector(getLayout);
    const limits = useSelector(DBCommonStore.getLimits);
    const listJT = useSelector(getSelectedJT);
    const listSC = useSelector(getSelectedSC);
    const listWSK = useSelector(getSelectedWSK);
    const multiSections = useSelector(FiltersStore.getMultiSections);
    const options = useSelector(getOptions);
    const prediction = useSelector(DBCommonStore.getPrediction);
    const sections = useSelector(FiltersStore.getSections);
    const selectedDates = useSelector(DatesStore.getDatesSelected);
    const selectedJTs = useSelector(getSelectedTerritorialUnitsItems);
    const selectedIndicators = useSelector(IndicatorsStore.getSelectedIndicators);
    const types = useSelector(FiltersStore.getInfoTypes);

    const selectedDimensions = useSelector(FiltersStore.getSelectedDimensions);
    const selectedPositions = useSelector(FiltersStore.getSelectedPositions);
    const allDefaults = useSelector(FiltersStore.getAllDefaults);

    /** Charts Bars */
    const chartBarseriesColorsDefault = useSelector(ChartsBarsSettingsStore.getSeriesColorsDefault);
    /** /Charts Bars */
    /** / Redux states */

    /** Component states */
    const [ARE_UPDATES, setARE_UPDATES] = useState(false);

    const [CUSTOM_CURRENT_INDICATOR, setCUSTOM_CURRENT_INDICATOR] = useState(null);
    const [CUSTOM_SELECTED_DATES, setCUSTOM_SELECTED_DATES] = useState([]);

    /** - defaults */
    const [LOADING_DEFAULTS, setLOADING_DEFAULTS] = useState(false);
    const [ERROR_DEFAULTS, setERROR_DEFAULTS] = useState(false);
    const [DEFAULTS, setDEFAULTS] = useState(null);

    /** - sections */
    const [LOADING_SECTIONS, setLOADING_SECTIONS] = useState(false);
    const [ERROR_SECTIONS, setERROR_SECTIONS] = useState(false);
    const [CUSTOM_SECTIONS, setCUSTOM_SECTIONS] = useState(null);
    const [CUSTOM_CURRENT_SECTION, setCUSTOM_CURRENT_SECTION] = useState(null);
    const [CUSTOM_MULTI_SECTIONS, setCUSTOM_MULTI_SECTIONS] = useState({ enabled: false });
    const [CUSTOM_MULTI_MODE, setCUSTOM_MULTI_MODE] = useState(false);

    /** - types */
    const [LOADING_TYPES, setLOADING_TYPES] = useState(false);
    const [ERROR_TYPES, setERROR_TYPES] = useState(false);
    const [CUSTOM_TYPES, setCUSTOM_TYPES] = useState(types);

    /** - dimensions */
    const [LOADING_DIMENSIONS, setLOADING_DIMENSIONS] = useState(false);
    const [ERROR_DIMENSIONS, setERROR_DIMENSIONS] = useState(false);
    const [CUSTOM_DIMENSIONS, setCUSTOM_DIMENSIONS] = useState(null);
    const [CUSTOM_SELECTED_DIMENSION, setCUSTOM_SELECTED_DIMENSION] = useState(null);

    /** - positions */
    const [CUSTOM_SELECTED_POSITIONS, setCUSTOM_SELECTED_POSITIONS] = useState(null);

    /** - other */
    const [isToBeApplied, setIsToBeApplied] = useState(false);
    const [isToBeReset, setIsToBeReset] = useState(false);
    const [ZOOMED, setZOOMED] = useState({ types: false, dimensions: false });
    /** / end Component states */

    /** Handlers */
    const handleOnSetSections = ({ newSections }) => console.log("handleOnSetSections: ", newSections);

    const handleOnSetCurrentSection = ({ newSection }) => {
        setARE_UPDATES(true);
        setCUSTOM_CURRENT_SECTION(newSection);
    };

    const handleMultiSections = (event) => {
        let _newCustomMultiSections = JSON.parse(JSON.stringify(CUSTOM_MULTI_SECTIONS));
        if (!event.target.checked) {
            _newCustomMultiSections[CUSTOM_CURRENT_INDICATOR.indicator_id] = [
                CUSTOM_CURRENT_SECTION[CUSTOM_CURRENT_INDICATOR.indicator_id]?.id_section,
            ];
        }

        setCUSTOM_MULTI_SECTIONS({ ..._newCustomMultiSections, enabled: event.target.checked });
    };

    const handleOnSetMultiSections = ({ newSections }) => {
        setARE_UPDATES(true);
        setCUSTOM_MULTI_SECTIONS(newSections);
    };

    const handleOnCheckType = ({ newTypes }) => {
        setARE_UPDATES(true);
        setCUSTOM_TYPES(newTypes);
    };

    const handleOnSelectDimension = ({ newDimensions, dims, selected, newSelectedPositions, selectedDimension }) => {
        const _expanded = selected?.expanded;
        // console.log("handleOnSelectDimension", {
        //     newDimensions,
        //     dims,
        //     selected,
        //     newSelectedPositions,
        //     selectedDimension,
        //     _expanded,
        // });
        if (JSON.stringify(CUSTOM_DIMENSIONS) !== JSON.stringify(newDimensions)) {
            setCUSTOM_DIMENSIONS(newDimensions);
        }

        setCUSTOM_SELECTED_DIMENSION(selectedDimension);

        if (dims && selected) {
            const _firstId = selected?.children?.[0]?.position_id;
            if (_firstId) {
                // dispatch(FiltersStore.setExpandedPositions([_firstId]));
                dispatch(FiltersStore.setExpandedPositions(_expanded?.length ? _expanded : [_firstId]));
            }
        }
    };

    const handleOnSelectPosition = ({ newDimensions, dims, selected, newSelectedPositions, selectedDimension }) => {
        setARE_UPDATES(ARE_UPDATES || !dims);
        // setCUSTOM_DIMENSIONS(newDimensions);
        setCUSTOM_SELECTED_POSITIONS(newSelectedPositions);

        if (dims && selected) {
            const _firstId = selected?.children?.[0]?.position_id;
            if (_firstId) {
                dispatch(FiltersStore.setExpandedPositions([_firstId]));
            }
        }
    };

    const handleOnReset = async () => {
        setLOADING_DIMENSIONS(true);

        let _defaultSelectedPositions = [];

        const _indicatorIds = Object.keys(allDefaults?._dimensions);

        if (_indicatorIds?.length) {
            _indicatorIds?.forEach((_indId) => {
                const _sectionIds = Object.keys(allDefaults?._dimensions?.[_indId]);

                if (_sectionIds?.length) {
                    _sectionIds?.forEach((_secId) => {
                        allDefaults?._dimensions?.[_indId]?.[_secId]?.forEach((_dim) => {
                            const _defaultPositions = _dim?.children?.filter((child) => child.checked);

                            if (_defaultPositions?.length) {
                                _defaultPositions?.forEach((_pos) => {
                                    const agregate_id = `${_indId}-${_secId}-${_pos?.dimension_id}-${_pos?.position_id}`;
                                    const agregate_min_id = `${_pos?.dimension_id}-${_pos?.position_id}`;
                                    const _selectedItem = {
                                        ..._pos,
                                        indicator_id: Number(_indId),
                                        section_id: Number(_secId),
                                        dimension_id: _pos?.dimension_id,
                                        position_id: _pos?.position_id,
                                        agregate_id,
                                        agregate_min_id,
                                    };
                                    _defaultSelectedPositions.push(_selectedItem);
                                });
                            }
                        });
                    });
                }
            });
        }

        _defaultSelectedPositions = uniqBy(_defaultSelectedPositions, "agregate_id")
            ?.filter((f) => f.agregate_id)
            ?.sort((a, b) => a.agregate_id - b.agregate_id);

        setCUSTOM_SECTIONS(allDefaults?._sections);

        setCUSTOM_CURRENT_INDICATOR(currentIndicator);

        setCUSTOM_CURRENT_SECTION(allDefaults?._current_section);
        setCUSTOM_MULTI_SECTIONS(allDefaults?._multiSections);

        setCUSTOM_TYPES(allDefaults?._infoTypes);

        // setCUSTOM_DIMENSIONS(allDefaults?._dimensions);
        setCUSTOM_DIMENSIONS(null);

        // setCUSTOM_SELECTED_DIMENSION(_selectedDimension);

        setCUSTOM_SELECTED_POSITIONS(allDefaults?._selectedPositions);
        // setCUSTOM_SELECTED_POSITIONS(_defaultSelectedPositions);

        setARE_UPDATES(true);

        setTimeout(() => {
            setLOADING_DIMENSIONS(false);
        }, 10);

        /** unfold all positions */
        // dispatch(FiltersStore.setExpandedPositions([]));

        // setTimeout(()=>{
        //     handleOnApply()
        // },1000)
    };

    const handleOnApply = (withScroll) => {
        // process.env.NODE_ENV === "development" && console.log("handleOnApply");
        const _tableContainer = document.getElementById(`db-main-table`);

        if (_tableContainer && withScroll) {
            setTimeout(() => {
                _tableContainer?.scrollIntoView({ block: "start", behavior: "smooth" });
            }, 500);
        }

        dispatch(FiltersStore.setSections(CUSTOM_SECTIONS));
        dispatch(FiltersStore.setCurrentSection(CUSTOM_CURRENT_SECTION));
        dispatch(FiltersStore.setMultiSections(CUSTOM_MULTI_SECTIONS));
        dispatch(FiltersStore.setInfoTypes(CUSTOM_TYPES));
        dispatch(FiltersStore.setDimensions(CUSTOM_DIMENSIONS));

        dispatch(FiltersStore.setSelectedDimensions(CUSTOM_SELECTED_DIMENSION));
        dispatch(FiltersStore.setSelectedPositions(CUSTOM_SELECTED_POSITIONS));
        dispatch(FiltersStore.setFiltersChanged(true));

        /** Reset Bars chart series colors */
        dispatch(ChartsBarsSettingsStore.setSeriesColors(chartBarseriesColorsDefault));

        setARE_UPDATES(false);
    };

    const handleZoom = ({ elem }) => {
        setZOOMED({
            ...ZOOMED,
            [elem]: !ZOOMED[elem],
        });
    };
    /** / end Handlers */

    /** Effects */
    /** - loading modules */
    useEffect(() => {
        dispatch(DBCommonStore.setLoadingModules({ key: "tDefaults", value: LOADING_DEFAULTS }));
    }, [LOADING_DEFAULTS]);

    useEffect(() => {
        dispatch(DBCommonStore.setLoadingModules({ key: "tSections", value: LOADING_SECTIONS }));
    }, [LOADING_SECTIONS]);

    useEffect(() => {
        dispatch(DBCommonStore.setLoadingModules({ key: "tTypes", value: LOADING_TYPES }));
    }, [LOADING_TYPES]);

    useEffect(() => {
        dispatch(DBCommonStore.setLoadingModules({ key: "tDimensionsAndPositions", value: LOADING_DIMENSIONS }));
    }, [LOADING_DIMENSIONS]);
    /** - /loading modules */

    useEffect(() => onLoadingDefaults(LOADING_DEFAULTS), [LOADING_DEFAULTS]);

    useEffect(() => onIsDefaultsError(ERROR_DEFAULTS), [ERROR_DEFAULTS]);

    useEffect(() => setDEFAULTS(defaults), [defaults]);

    useEffect(() => setCUSTOM_SECTIONS(sections ? { ...sections } : null), [sections]);

    useEffect(() => {
        setCUSTOM_CURRENT_SECTION(currentSection ? { ...currentSection } : null);
    }, [currentSection, tableOrChart]);

    useEffect(() => setCUSTOM_MULTI_SECTIONS({ ...multiSections }), [multiSections]);

    useEffect(() => setCUSTOM_MULTI_MODE(CUSTOM_MULTI_SECTIONS?.enabled), [CUSTOM_MULTI_SECTIONS]);

    useEffect(() => {
        setCUSTOM_CURRENT_INDICATOR({ ...currentIndicator });
    }, [currentIndicator]);

    useEffect(() => setCUSTOM_SELECTED_DATES([...selectedDates]), [selectedDates]);

    /** - set CUSTOM_* types*/
    useEffect(() => {
        setCUSTOM_TYPES(types);
    }, [types]);

    /** - set CUSTOM_* dimensions*/
    useEffect(() => {
        // if (!CUSTOM_DIMENSIONS) setCUSTOM_DIMENSIONS(dimensions);

        // if (JSON.stringify(CUSTOM_DIMENSIONS) !== JSON.stringify(dimensions)) {
        //     setCUSTOM_DIMENSIONS(dimensions);
        // }
        if (
            !CUSTOM_DIMENSIONS ||
            Object.keys(CUSTOM_DIMENSIONS || {})?.length !== Object.keys(dimensions || {})?.length
        ) {
            setCUSTOM_DIMENSIONS(dimensions);
        }
    }, [CUSTOM_DIMENSIONS, dimensions]);

    /** - set CUSTOM_SELECTED_DIMENSION */
    useEffect(() => {
        if (!CUSTOM_SELECTED_DIMENSION) setCUSTOM_SELECTED_DIMENSION(selectedDimensions);
    }, [CUSTOM_SELECTED_DIMENSION, selectedDimensions]);

    /** - set CUSTOM_SELECTED_POSITIONS */
    useEffect(() => {
        if (!CUSTOM_SELECTED_POSITIONS) setCUSTOM_SELECTED_POSITIONS(selectedPositions);
    }, [CUSTOM_SELECTED_POSITIONS, selectedPositions]);

    /** - send CUSTOM_SELECTED_POSITIONS */
    useEffect(() => {
        onSelectPositions && onSelectPositions(CUSTOM_SELECTED_POSITIONS);
    }, [CUSTOM_SELECTED_POSITIONS]);

    /** - Collect defaults from API */
    useEffect(() => {
        if (defaults || LOADING_DEFAULTS || !selectedIndicators.length || !selectedDates.length) return;

        setLOADING_DEFAULTS(true);

        const indicators = _.uniq(selectedIndicators.map((i) => i.indicator_id));
        const years = _.uniq(selectedDates.map((d) => d.date_id));
        const periods = _.uniq(selectedDates.flatMap((d) => d.tab_period_id));
        const customer_section = customerSection;
        const customer_dimension = customerDimension;
        const customer_position = customerPosition;

        handleGetDefaultMetaTableFromAPI({
            indicators,
            years,
            periods,
            customer_section,
            customer_dimension,
            customer_position,
        });
    }, [
        defaults,
        currentSection,
        currentIndicator,
        dimensions,
        multiSections,
        sections,
        selectedDates,
        selectedIndicators,
        types,
    ]);

    /** - Collect sections from API ??? */
    useEffect(() => {
        // return;
        const indicatorId = CUSTOM_CURRENT_INDICATOR?.indicator_id;
        const years = _.uniq(CUSTOM_SELECTED_DATES?.map((d) => d.date_id));
        const periods = _.uniq(CUSTOM_SELECTED_DATES?.flatMap((d) => d.tab_period_id));

        if (!indicatorId || !years?.length || !periods?.length) return;

        const isLoaded = CUSTOM_SECTIONS && CUSTOM_SECTIONS[indicatorId]?.find((s) => s.loaded);

        if (isLoaded) return;

        setLOADING_SECTIONS(true);
        setERROR_SECTIONS(false);

        // const _newSections = handleGetVariableSectionsFromAPI({ indicatorId, years, periods }).then((res) => {
        //     console.log(res);
        // });

        getVariableSectionsFromAPI({ indicatorId, years, periods })
            .then((result) => {
                if (result.message && result.message.type === "Error") {
                    setERROR_SECTIONS(result);
                } else {
                    const newSections = {
                        ...sections,
                        [indicatorId]: result?.map((r) => ({ ...r, loaded: true })),
                    };

                    setCUSTOM_SECTIONS(newSections);

                    if (JSON.stringify(sections) !== JSON.stringify(newSections)) {
                        dispatch(FiltersStore.setSections(newSections));
                    }
                }
            })
            .catch((error) => {
                console.log("%c getVariableSectionsFromAPI error: ", "background: #F00; color:#FFF", error);
            })
            .finally(() => {
                setLOADING_SECTIONS(false);
            });
    }, [CUSTOM_CURRENT_INDICATOR, CUSTOM_SECTIONS, currentSection, sections]);

    /** - Collect types from API */
    useEffect(() => {
        if (!CUSTOM_CURRENT_SECTION || !types) return;

        const indicatorId = CUSTOM_CURRENT_INDICATOR?.indicator_id;
        const sectionId = CUSTOM_CURRENT_SECTION[indicatorId]?.id_section;
        const years = _.uniq(CUSTOM_SELECTED_DATES?.map((d) => d.date_id));
        const periods = _.uniq(CUSTOM_SELECTED_DATES?.flatMap((d) => d.tab_period_id));

        const multiSectionIds = CUSTOM_MULTI_SECTIONS[indicatorId] || [];

        if (!indicatorId || !sectionId || !years?.length || !periods?.length) return;

        setERROR_DIMENSIONS(false);

        setLOADING_TYPES(true);
        setERROR_TYPES(false);
        if (multiSectionIds?.length) {
            multiSectionIds.forEach((sectionId) => {
                if (CUSTOM_TYPES && CUSTOM_TYPES[indicatorId]?.find((t) => t.id_section === sectionId)?.loaded) {
                    setLOADING_TYPES(false);
                    return;
                }

                handleGetTypeOfInformationFromAPI({ indicatorId, sectionId, years, periods });
            });
        }
    }, [
        CUSTOM_CURRENT_INDICATOR,
        CUSTOM_CURRENT_SECTION,
        CUSTOM_MULTI_MODE,
        CUSTOM_TYPES,
        types,
        CUSTOM_MULTI_SECTIONS,
    ]);

    /** - Collect dimensions from API */
    useEffect(() => {
        if (!CUSTOM_CURRENT_SECTION || !dimensions) return;

        const indicatorId = CUSTOM_CURRENT_INDICATOR?.indicator_id;
        let sectionId = CUSTOM_CURRENT_SECTION[indicatorId]?.id_section;

        const sectionIds = [sectionId].filter((f) => f);
        const multiSectionIds = CUSTOM_MULTI_SECTIONS[indicatorId];

        if (!indicatorId || !sectionId || !sectionIds?.length) return;

        if (ERROR_DIMENSIONS) return;

        const isLoaded = dimensions?.[indicatorId]?.[sectionId]?.[0]?.loaded === i18n.language;

        // if (isLoaded) return;

        setLOADING_DIMENSIONS(true);
        setERROR_DIMENSIONS(false);

        if (multiSectionIds?.length) {
            multiSectionIds.forEach((sectionId) => {
                if (dimensions?.[indicatorId]?.[sectionId]?.[0]?.loaded === i18n.language) {
                    setLOADING_DIMENSIONS(false);
                    return;
                }
                handleGetDimensionsAndPositions({ sectionId, indicatorId });
            });
        }
    }, [
        CUSTOM_CURRENT_INDICATOR,
        CUSTOM_CURRENT_SECTION,
        // CUSTOM_DIMENSIONS,
        dimensions,
        ERROR_DIMENSIONS,
        CUSTOM_MULTI_SECTIONS,
        i18n.language,
    ]);

    /** Watch for changes */
    useEffect(() => {
        const toBeApplied = prediction?.table?._accepted !== false && ARE_UPDATES;

        setIsToBeApplied(toBeApplied);
        onIsToBeApplied(toBeApplied);

        const toBeReset =
            toBeApplied || !!currentSection || JSON.stringify(multiSections) !== JSON.stringify({ enabled: false });

        setIsToBeReset(toBeReset);
    }, [
        currentSection,
        CUSTOM_CURRENT_SECTION,
        multiSections,
        CUSTOM_MULTI_SECTIONS,
        types,
        CUSTOM_TYPES,
        dimensions,
        CUSTOM_DIMENSIONS,
        tableOrChart,
        prediction,
        ARE_UPDATES,
    ]);

    /** Count */
    useEffect(() => {
        if (
            !CUSTOM_TYPES ||
            !CUSTOM_DIMENSIONS ||
            !selectedDates ||
            !CUSTOM_MULTI_SECTIONS ||
            !selectedIndicators ||
            !selectedJTs ||
            !layout ||
            !options
        )
            return;

        const params = {
            // types: CUSTOM_TYPES,
            // dimensions: CUSTOM_DIMENSIONS,
            dates: selectedDates,
            sections: CUSTOM_MULTI_SECTIONS,
            indicators: selectedIndicators,
            jts: selectedJTs,
            layout,
            options,
            limits,
            tableOrChart,
            from: "_Filters",
            listJT,
            listSC,
            listWSK,
            CUSTOM_SELECTED_POSITIONS,
        };

        onPredictionData(params);
        // console.log(` ---------------------------`);
        // console.log(` useEffect ~ params:`, params);
        // console.log(` ---------------------------`);
    }, [
        CUSTOM_TYPES,
        CUSTOM_DIMENSIONS,
        selectedDates,
        CUSTOM_MULTI_SECTIONS,
        selectedIndicators,
        selectedJTs,
        layout,
        options,
        limits,
        listJT,
        listSC,
        listWSK,
        CUSTOM_SELECTED_POSITIONS,
    ]);

    /** Apply */
    useEffect(() => {
        if (apply) {
            handleOnApply();
            onApply && onApply(false);
        }
    }, [apply]);

    /** / end Effects */

    const handleGetDefaultMetaTableFromAPI = async ({
        indicators,
        years,
        periods,
        customer_section,
        customer_dimension,
        customer_position,
        reset,
    }) => {
        const _defaults = await getDefaultMetaTableFromAPI({
            indicators,
            years,
            periods,
            customer_section,
            customer_dimension,
            customer_position,
        })
            .then(async (result) => {
                if (result?.message?.type === "Error") {
                    throw new Error(translationFromJson(result.message, "text"));
                    return;
                }

                if (result.message && result.message.type === "Error") {
                } else {
                    // let _sections = reset ? {} : JSON.parse(JSON.stringify(sections)) || {};
                    let _sections = reset ? {} : structuredClone(sections || {});

                    // const _resIndicatorIds = result?.map((r) => r.variable_id);

                    // let _allSections = await Promise.all(
                    //     _resIndicatorIds?.map(async (resIndicatorId) => {
                    //         const _getSection = await handleGetVariableSectionsFromAPI({
                    //             indicatorId: resIndicatorId,
                    //             years,
                    //             periods,
                    //         });

                    //         return _getSection;
                    //     })
                    // );

                    // _allSections?.forEach((section) => {
                    //     _sections = {
                    //         ..._sections,
                    //         ...section,
                    //     };
                    // });

                    // console.log("_allSections: ", _allSections);

                    let _current_section = reset ? {} : JSON.parse(JSON.stringify(currentSection)) || {};
                    let _default_current_section;

                    const _multiSections = reset ? {} : JSON.parse(JSON.stringify(multiSections)) || {};
                    let _defaultMultiSections = structuredClone(_multiSections);

                    let _infoTypes = reset ? {} : JSON.parse(JSON.stringify(types)) || {};

                    let _dimensions = reset ? {} : JSON.parse(JSON.stringify(dimensions)) || {};

                    let _current_indicator_id;
                    let _current_indicator_section_id;

                    // let _selectedPositions = reset ? [] : structuredClone(CUSTOM_SELECTED_POSITIONS || []);
                    let _selectedPositions = reset
                        ? []
                        : structuredClone([...(CUSTOM_SELECTED_POSITIONS || []), ...(selectedPositions || [])]);

                    result.forEach(async (r) => {
                        const _indicator_id = r.variable_id;
                        const _section_id = r.section_id;
                        if (currentIndicator.indicator_id === _indicator_id) {
                            _current_indicator_id = r.variable_id;
                            _current_indicator_section_id = r.section_id;
                        }

                        _sections[_indicator_id] = _.uniqBy(
                            [
                                ...(_sections && _sections[_indicator_id] ? _sections[_indicator_id] : []),
                                {
                                    id_variable: _indicator_id,
                                    id_section: _section_id,
                                    order: 0,
                                    name: r.section_name,
                                    name_en: r.section_name_en,
                                    default: 1,
                                },
                            ],
                            "id_section"
                        );

                        _default_current_section = structuredClone(_current_section);
                        // _current_section[_indicator_id] = currentSection?.[_indicator_id]?.[_section_id]
                        _current_section[_indicator_id] = currentSection?.[_indicator_id] //?.[_section_id]
                            ? currentSection[_indicator_id]
                            : _sections?.[_indicator_id]?.find((s) => s.id_section === _section_id) ||
                              _sections[_indicator_id][0];

                        _default_current_section[_indicator_id] =
                            _sections?.[_indicator_id]?.find((s) => !!s.default) || _sections[_indicator_id][0];

                        // _multiSections[_indicator_id] = [_section_id];
                        _multiSections[_indicator_id] = _multiSections[_indicator_id] || [_section_id];
                        _defaultMultiSections[_indicator_id] = [_default_current_section[_indicator_id]?.id_section];

                        _infoTypes[_indicator_id] = _.uniqBy(
                            [
                                ...(reset ? [] : _infoTypes[_indicator_id] || []),
                                {
                                    checked: true,
                                    id_presentation: r.type_of_information_id,
                                    id_section: _section_id,
                                    id_variable: _indicator_id,
                                    name: r.type_of_information_name,
                                    name_en: r.type_of_information_name_en,
                                    precision: 2,
                                },
                            ],
                            "id_presentation"
                        );

                        _dimensions[_indicator_id] = {
                            ...(_dimensions[_indicator_id] || {}),
                            [_section_id]: r.dimensions.map((d) => {
                                const _children =
                                    (_dimensions[_indicator_id] && _dimensions[_indicator_id][_section_id])?.find(
                                        (f) => f.children?.length && f.children[0]?.dimension_id === d.dimension_id
                                    )?.children || [];

                                return {
                                    gr_dimension_id: d.dimension_id,
                                    gr_dimension_name: d.dimension_name,
                                    gr_dimension_name_en: d.dimension_name_en,
                                    name: d.dimension_name,
                                    name_en: d.dimension_name_en,
                                    selected: true,
                                    children: _.uniqBy(
                                        [
                                            ..._children,
                                            ...d.positions.map((p) => {
                                                const agregate_id = `${_indicator_id}-${_section_id}-${d.dimension_id}-${p.position_id}`;
                                                const agregate_min_id = `${d.dimension_id}-${p.position_id}`;
                                                const _selectedItem = {
                                                    indicator_id: _indicator_id,
                                                    section_id: _section_id,
                                                    dimension_id: d.dimension_id,
                                                    position_id: p.position_id,
                                                    agregate_id,
                                                    agregate_min_id,
                                                };
                                                _selectedPositions.push(_selectedItem);
                                                return {
                                                    checked: true,
                                                    dimension_id: d.dimension_id,
                                                    id: 1,
                                                    position_id: p.position_id,
                                                    position_name: p.position_name,
                                                    position_name_en: p.position_name_en,
                                                    position_order: 0,
                                                };
                                            }),
                                        ],
                                        "position_id"
                                    ),
                                };
                            }),
                        };
                    });

                    _selectedPositions = uniqBy(
                        _selectedPositions?.filter((f) => f),
                        "agregate_id"
                    )
                        ?.filter((f) => f.agregate_id)
                        ?.sort((a, b) => a.agregate_id - b.agregate_id);

                    /** get all types for current */
                    _infoTypes[_current_indicator_id] =
                        (await handleGetTypeOfInformationFromAPI({
                            indicatorId: _current_indicator_id,
                            sectionId: _current_indicator_section_id,
                            years,
                            periods,
                            reset,
                        })
                            .then((res) => {
                                return res[_current_indicator_id];
                            })
                            .catch((e) => {
                                console.log(e);
                                return false;
                            })) || _infoTypes[_current_indicator_id];

                    _dimensions[_current_indicator_id] =
                        (await handleGetDimensionsAndPositions({
                            indicatorId: _current_indicator_id,
                            sectionId: _current_indicator_section_id,
                            reset,
                        })
                            .then((res) => {
                                return res?.[_current_indicator_id];
                            })
                            .catch((e) => {
                                console.log(e);
                                return false;
                            })) || _dimensions[_current_indicator_id];

                    if (!reset) {
                        Promise.all([
                            dispatch(FiltersStore.setSections(_sections)),
                            dispatch(FiltersStore.setCurrentSection(_current_section)),
                            dispatch(FiltersStore.setMultiSections(_multiSections)),
                            dispatch(FiltersStore.setInfoTypes(_infoTypes)),
                            dispatch(FiltersStore.setDimensions(_dimensions)),
                            dispatch(DBCommonStore.setCustomerSection(null)),
                            dispatch(DBCommonStore.setCustomerDimension(null)),
                            dispatch(DBCommonStore.setCustomerPosition(null)),
                        ])
                            .then((res) => {
                                dispatch(FiltersStore.setDefaults(result));

                                _selectedPositions = uniqBy(
                                    _selectedPositions?.filter((f) => f),
                                    "agregate_id"
                                )
                                    ?.filter((f) => f.agregate_id)
                                    ?.sort((a, b) => a.agregate_id - b.agregate_id);

                                setCUSTOM_SELECTED_POSITIONS(_selectedPositions);
                                dispatch(FiltersStore.setSelectedPositions(_selectedPositions));

                                dispatch(FiltersStore.setFiltersChanged(true));
                            })

                            .catch((error) => {
                                console.log(error);
                            });
                    }

                    const _result = {
                        _sections,
                        _current_section: _default_current_section,
                        _multiSections: _defaultMultiSections,
                        _infoTypes,
                        _dimensions,
                        _selectedPositions,
                    };
                    // process.env.NODE_ENV === "development" &&
                    //     console.log("%c getDefaultMetaTableFromAPI return: ", "background: #CCC; color:#FFF", _result);

                    // process.env.NODE_ENV === "development" &&
                    //     console.log({
                    //         _sections,
                    //         _current_section,
                    //         _multiSections,
                    //         _infoTypes,
                    //         _dimensions,
                    //         _selectedPositions,
                    //     });

                    dispatch(FiltersStore.setAllDefaults(_result));

                    // return { _sections, _current_section, _multiSections, _infoTypes, _dimensions, _selectedPositions };
                    return _result;
                }
            })
            .catch((error) => {
                console.log("%c getDefaultMetaTableFromAPI error: ", "background: #F00; color:#FFF", error);
                setERROR_DEFAULTS(error.message);
            })
            .finally(() => {
                setLOADING_DEFAULTS(false);
            });

        return _defaults;
    };

    const handleGetVariableSectionsFromAPI = async ({ indicatorId, years, periods }) => {
        setLOADING_SECTIONS(true);
        setERROR_SECTIONS(false);

        const _sections = await getVariableSectionsFromAPI({ indicatorId, years, periods })
            .then((result) => {
                if (result.message && result.message.type === "Error") {
                    setERROR_SECTIONS(result);
                } else {
                    const newSections = {
                        ...sections,
                        [indicatorId]: result?.map((r) => ({ ...r, loaded: true })),
                    };

                    setCUSTOM_SECTIONS(newSections);

                    if (JSON.stringify(sections) !== JSON.stringify(newSections)) {
                        dispatch(FiltersStore.setSections(newSections));
                    }

                    return newSections;
                }
            })
            .catch((error) => {
                console.log("%c getVariableSectionsFromAPI error: ", "background: #F00; color:#FFF", error);
            })
            .finally(() => {
                setLOADING_SECTIONS(false);
            });

        return _sections;
    };

    const handleGetTypeOfInformationFromAPI = async ({ indicatorId, sectionId, years, periods, reset }) => {
        const _types = await getTypeOfInformationFromAPI({ indicatorId, sectionId, years, periods })
            .then((result) => {
                if (result.message && result.message.type === "Error") {
                    setERROR_TYPES(result);
                } else {
                    /** If no one is checked by default, let's check first */
                    if (result?.length && !result.find((r) => r.checked)) {
                        result[0] = { ...result[0], checked: true };
                    }

                    let newTypes = JSON.parse(JSON.stringify({ ...types, ...CUSTOM_TYPES })) || {};

                    newTypes[indicatorId] = [
                        ...(CUSTOM_MULTI_MODE ? newTypes[indicatorId] || [] : []),
                        ...result.map((r) => ({
                            ...r,
                            loaded: true,
                            checked: !!(reset
                                ? r.checked
                                : newTypes &&
                                  newTypes[indicatorId]?.find(
                                      (t) => t.id_presentation === r.id_presentation && t.id_section === r.id_section
                                  )
                                ? newTypes[indicatorId].find(
                                      (t) => t.id_presentation === r.id_presentation && t.id_section === r.id_section
                                  )?.checked
                                : r.checked),
                        })),
                    ];

                    newTypes[indicatorId] = _.uniqBy(newTypes[indicatorId], (u) => {
                        return `${u.id_section}-${u.id_presentation}`;
                    });

                    if (JSON.stringify(CUSTOM_TYPES) !== JSON.stringify(newTypes)) {
                        setCUSTOM_TYPES(newTypes);
                    }

                    return newTypes;
                }
            })
            .catch((error) => {
                console.log("%c getTypeOfInformationFromAPI error: ", "background: #F00; color:#FFF", error);
            })
            .finally(() => {
                setLOADING_TYPES(false);
            });

        return _types;
    };

    const handleGetDimensionsAndPositions = async ({ sectionId, indicatorId, reset, force }) => {
        setLOADING_DIMENSIONS(true);

        const _dims = await getDimensionsAndPositions({ sectionIds: [sectionId], lang: i18n.language })
            .then((result) => {
                if (result.message && result.message.type === "Error") {
                    const _new_custom_dimensions = {
                        ...(CUSTOM_DIMENSIONS || {}),
                        [indicatorId]: {
                            ...(CUSTOM_DIMENSIONS ? CUSTOM_DIMENSIONS[indicatorId] : {}),
                            [sectionId]: [],
                        },
                    };
                    if (JSON.stringify(CUSTOM_DIMENSIONS) !== JSON.stringify(_new_custom_dimensions)) {
                        setCUSTOM_DIMENSIONS(_new_custom_dimensions);
                    }
                    setERROR_DIMENSIONS(result);
                    setLOADING_DIMENSIONS(false);
                } else {
                    setERROR_DIMENSIONS(false);

                    let _selectedDimension;
                    let _expanded_positions = result
                        .flatMap((r) => {
                            return r.dimensions?.flatMap((d) => d.expanded_positions);
                        })
                        ?.filter((f) => f);

                    // console.log(_expanded_positions);

                    dispatch(FiltersStore.setExpandedPositions([..._expanded_positions]));

                    const newDimensions = result.map((group) => {
                        const isDimensionSelected = () => {
                            return reset
                                ? !!group.selected
                                : CUSTOM_DIMENSIONS &&
                                  CUSTOM_DIMENSIONS[indicatorId] &&
                                  CUSTOM_DIMENSIONS[indicatorId][sectionId]
                                ? CUSTOM_DIMENSIONS[indicatorId][sectionId].find(
                                      (d) => d.gr_dimension_id === group.gr_dimension_id
                                  )?.selected
                                : !!group.selected;
                        };

                        const isPositionChecked = ({ dimension_id, position_id, org_checked }) => {
                            if (
                                CUSTOM_DIMENSIONS &&
                                CUSTOM_DIMENSIONS[indicatorId] &&
                                CUSTOM_DIMENSIONS[indicatorId][sectionId] &&
                                !reset
                            ) {
                                const dimension = CUSTOM_DIMENSIONS[indicatorId][sectionId].find(
                                    (d) => d.gr_dimension_id === dimension_id
                                );

                                const isChecked = dimension?.children?.find(
                                    (p) => p.position_id === position_id
                                )?.checked;

                                return typeof isChecked === "undefined" ? org_checked : isChecked;
                            } else {
                                return org_checked;
                            }
                        };

                        const newGroup = {
                            ...group,
                            // loaded: true,
                            loaded: i18n.language,
                            selected: isDimensionSelected(),
                            name: group.gr_dimension_name,
                            name_en: group.gr_dimension_name_en,
                            expanded: group.dimensions?.flatMap((d) => d.expanded_positions)?.filter((f) => f),
                            children: group.dimensions?.length
                                ? group.dimensions.map((dimension) => {
                                      const positions = [];
                                      if (dimension.dimension_positions?.length) {
                                          dimension.dimension_positions.forEach((position) => {
                                              const _newPosition = {
                                                  ...position,
                                                  indicator_id: indicatorId,
                                                  section_id: sectionId,
                                                  dimension_id: dimension.dimension_id,
                                                  checked: !!isPositionChecked({
                                                      dimension_id: dimension.dimension_id,
                                                      position_id: position.position_id,
                                                      org_checked: !!position.checked,
                                                  }),
                                              };
                                              //   positions.push({
                                              //       ...position,
                                              //       indicator_id: indicatorId,
                                              //       section_id: sectionId,
                                              //       dimension_id: dimension.dimension_id,
                                              //       checked: !!isPositionChecked({
                                              //           dimension_id: dimension.dimension_id,
                                              //           position_id: position.position_id,
                                              //           org_checked: position.checked,
                                              //       }),
                                              //   });

                                              positions.push(_newPosition);
                                          });
                                      }
                                      return _.uniqBy(positions, "position_id");
                                  })[0]
                                : [],
                            dimensions: undefined,
                        };
                        delete newGroup.dimensions;

                        newGroup.flatChildren = getAllIDs(newGroup.children);

                        return newGroup;
                    });

                    _selectedDimension = structuredClone(newDimensions?.find((d) => d.selected) || newDimensions?.[0]);
                    delete _selectedDimension.children;
                    delete _selectedDimension.flatChildren;

                    const _new_custom_dimensions = {
                        ...dimensions,
                        ...CUSTOM_DIMENSIONS,
                        [indicatorId]: {
                            ...(dimensions ? dimensions[indicatorId] : {}),
                            ...(CUSTOM_DIMENSIONS ? CUSTOM_DIMENSIONS[indicatorId] : {}),
                            [sectionId]: newDimensions,
                        },
                    };

                    let _checkedPositions = newDimensions?.flatMap((dimension) => {
                        // return dimension?.flatChildren
                        return getAllIDs(dimension?.children)
                            ?.filter((child) => child.checked)
                            ?.map((child) => {
                                const indicator_id = child.indicator_id || indicatorId;
                                const section_id = child.section_id || sectionId;

                                child.indicator_id = indicator_id;
                                child.section_id = section_id;

                                return {
                                    ...child,
                                    indicator_id,
                                    section_id,
                                    // agregate_id: `${child.indicator_id}-${child.section_id}-${child.dimension_id}-${child.position_id}`,
                                    agregate_id: `${indicator_id}-${section_id}-${child.dimension_id}-${child.position_id}`,
                                    agregate_min_id: `${child.dimension_id}-${child.position_id}`,
                                    children: undefined,
                                };
                            });
                    });

                    const _newDefaultSelectedPositions = uniqBy(
                        [...(allDefaults?._selectedPositions || []), ..._checkedPositions],
                        "agregate_id"
                    )
                        ?.filter((f) => f.agregate_id)
                        ?.sort((a, b) => a.agregate_id - b.agregate_id);

                    const _newAllDefaults = {
                        ...(allDefaults || {}),
                        _selectedPositions: _newDefaultSelectedPositions,
                    };

                    dispatch(FiltersStore.setAllDefaults(_newAllDefaults));

                    // _checkedPositions = uniqBy([...(selectedPositions || []), ..._checkedPositions], "agregate_id")
                    //     ?.filter((f) => f.agregate_id)
                    //     ?.sort((a, b) => a.agregate_id - b.agregate_id);
                    _checkedPositions = uniqBy(
                        [...(selectedPositions || []), ...(CUSTOM_SELECTED_POSITIONS || []), ..._checkedPositions],
                        "agregate_id"
                    )
                        ?.filter((f) => f.agregate_id)
                        ?.sort((a, b) => a.agregate_id - b.agregate_id);

                    dispatch(FiltersStore.setSelectedDimensions(_selectedDimension));

                    dispatch(FiltersStore.setSelectedPositions(_checkedPositions));

                    /** https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/383 */
                    // CUSTOM_SELECTED_POSITIONS?.length || setCUSTOM_SELECTED_POSITIONS(_checkedPositions);

                    /** https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/383 +
                     * https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/418
                     */
                    // if (
                    //     !CUSTOM_SELECTED_POSITIONS?.length ||
                    //     !CUSTOM_SELECTED_POSITIONS?.map((s) => s.section_id)?.includes(sectionId)
                    // ) {
                    //     setCUSTOM_SELECTED_POSITIONS(_checkedPositions);
                    // }

                    /** https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/383 +
                     * https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/418 +
                     * https://git.stat.intra/cis/wud/otwartedane/frontend/-/issues/419
                     */
                    if (
                        !CUSTOM_SELECTED_POSITIONS?.length ||
                        !CUSTOM_SELECTED_POSITIONS?.map((s) => `${s.indicator_id}-${s.section_id}`)?.includes(
                            `${indicatorId}-${sectionId}`
                        )
                    ) {
                        setCUSTOM_SELECTED_POSITIONS(_checkedPositions);
                    }

                    if (JSON.stringify(CUSTOM_DIMENSIONS) !== JSON.stringify(_new_custom_dimensions)) {
                        setCUSTOM_DIMENSIONS(_new_custom_dimensions);
                    }

                    if (JSON.stringify(dimensions) !== JSON.stringify(_new_custom_dimensions)) {
                        dispatch(FiltersStore.setDimensions(_new_custom_dimensions));
                        dispatch(FiltersStore.setLoadedLang(i18n.language));
                    }

                    setLOADING_DIMENSIONS(false);

                    return _new_custom_dimensions;
                }
            })
            .catch((error) => {
                console.log("%c getDimensionsAndPositions error: ", "background: #F00; color:#FFF", error);
            })
            .finally(() => {
                // setLOADING_DIMENSIONS(false);
            });

        return _dims;
    };

    return (
        <Container maxWidth={false} disableGutters>
            <Grid container spacing={0}>
                <Grid
                    item
                    xs={12}
                    sx={{ position: "relative" }}
                    className={clsx(
                        tableOrChart === "chart" && CUSTOM_CURRENT_INDICATOR?.hidden && classes.disabledColors
                    )}
                >
                    {tableOrChart === "chart" && CUSTOM_CURRENT_INDICATOR?.hidden && (
                        <div className={classes.hiddenOverlay} />
                    )}

                    <FiltersAccordion
                        onReset={() => handleOnReset()}
                        isToBeApplied={isToBeApplied}
                        isToBeReset={isToBeReset}
                        onApply={() => handleOnApply(true)}
                        prediction={prediction}
                    >
                        <Grid container spacing={1} className={clsx(classes.gridContainerSections)}>
                            <Grid item xs={12} sm={12} className={classes.gridItemSections}>
                                <Typography variant={"body1"} className={classes.darkTitle}>
                                    {CUSTOM_MULTI_MODE ? t("Przekroje") : t("Przekroj")}
                                    {":"}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} className={classes.gridItemVertCntr}>
                                <SectionsSelect
                                    currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                    currentSection={CUSTOM_CURRENT_SECTION}
                                    isError={ERROR_SECTIONS}
                                    isLoading={
                                        LOADING_SECTIONS ||
                                        !(
                                            CUSTOM_CURRENT_SECTION &&
                                            CUSTOM_CURRENT_SECTION[CUSTOM_CURRENT_INDICATOR?.indicator_id]
                                        )
                                    }
                                    isMultisectionMode={CUSTOM_MULTI_MODE}
                                    multiSections={CUSTOM_MULTI_SECTIONS}
                                    selectedDates={CUSTOM_SELECTED_DATES}
                                    sections={CUSTOM_SECTIONS}
                                    onSetCurrentSection={(newSection) => handleOnSetCurrentSection({ newSection })}
                                    onSetMultiSections={(newSections) => handleOnSetMultiSections({ newSections })}
                                    onSetSections={(newSections) => handleOnSetSections({ newSections })}
                                />
                                <FormControlLabel
                                    value="start"
                                    control={
                                        <Switch
                                            checked={CUSTOM_MULTI_SECTIONS?.enabled}
                                            onChange={handleMultiSections}
                                            color="primary"
                                        />
                                    }
                                    label={t("Wybierz kilka przekrojów")}
                                    labelPlacement="start"
                                    classes={{
                                        root: classes.fclRoot,
                                        label: clsx(classes.multiLabel, classes.darkTitle),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={isMobile ? 5 : isTablet ? 2 : 4}>
                            <Grid item xs={12} sm={5} md={4} lg={4}>
                                <Grid container spacing={0} style={{ height: "100%" }}>
                                    <Grid item xs={12} sx={{ position: "relative" }}>
                                        <Grid item xs={12} className={classes.gridItemSections}>
                                            <Typography variant={"body1"} className={classes.darkTitle}>
                                                {t("Typ informacji")}
                                                {": "}
                                            </Typography>
                                        </Grid>
                                        <AppTooltip
                                            title={t("Otwórz wybór typu informacji w dużym oknie")}
                                            arrow
                                            placement={"top"}
                                            type={"dark"}
                                            classes={{ tooltip: classes.tooltip }}
                                        >
                                            <IconButton
                                                className={classes.zoomBtn}
                                                onClick={() => handleZoom({ elem: "types" })}
                                                aria-label={t("Otwórz wybór typu informacji w dużym oknie")}
                                            >
                                                <LaunchIcon color={"primary"} />
                                            </IconButton>
                                        </AppTooltip>
                                        {/* <pre>{JSON.stringify(CUSTOM_TYPES,null,4)}</pre> */}
                                        <InformationType
                                            currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                            currentSection={CUSTOM_CURRENT_SECTION}
                                            isError={ERROR_TYPES}
                                            infoTypes={CUSTOM_TYPES}
                                            isLoading={LOADING_TYPES}
                                            selectedDates={CUSTOM_SELECTED_DATES}
                                            tableOrChart={tableOrChart}
                                            onCheckType={(newTypes) => handleOnCheckType({ newTypes })}
                                            onZoom={() => handleZoom({ elem: "types" })}
                                        />
                                        {ZOOMED.types ? (
                                            <TypesZoomDialog
                                                open={ZOOMED.types}
                                                setOpen={() => handleZoom({ elem: "types" })}
                                            >
                                                <InformationType
                                                    currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                                    currentSection={CUSTOM_CURRENT_SECTION}
                                                    isError={ERROR_TYPES}
                                                    infoTypes={CUSTOM_TYPES}
                                                    isLoading={LOADING_TYPES}
                                                    selectedDates={CUSTOM_SELECTED_DATES}
                                                    tableOrChart={tableOrChart}
                                                    onCheckType={(newTypes) => handleOnCheckType({ newTypes })}
                                                    isZoomed={ZOOMED.types}
                                                />
                                            </TypesZoomDialog>
                                        ) : null}
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={7} md={8} lg={8}>
                                <Grid container spacing={0} style={{ height: "100%", position: "relative" }}>
                                    <Grid item xs={12} className={classes.gridItemSections}>
                                        <Typography
                                            variant={"body1"}
                                            className={classes.dimTitle}
                                            sx={{ mt: { sm: 1, md: 0 } }}
                                        >
                                            {mdDown && !isMobile ? t("Wymiary i pozycje") : t("Wymiar")}
                                            {": "}
                                        </Typography>
                                    </Grid>
                                    <AppTooltip
                                        title={t("Otwórz wybór wymiaru i pozycji w dużym oknie")}
                                        arrow
                                        placement={"top"}
                                        type={"dark"}
                                        classes={{ tooltip: classes.tooltip }}
                                    >
                                        <IconButton
                                            className={clsx(classes.zoomBtn)}
                                            onClick={() => handleZoom({ elem: "dimensions" })}
                                            aria-label={t("Otwórz wybór wymiaru i pozycji w dużym oknie")}
                                        >
                                            <LaunchIcon color={"primary"} />
                                        </IconButton>
                                    </AppTooltip>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={5}
                                        lg={4}
                                        xl={5}
                                        sx={{ height: { sm: "auto", md: "100%" } }}
                                    >
                                        <Dimensions
                                            currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                            currentSection={CUSTOM_CURRENT_SECTION}
                                            dimensions={CUSTOM_DIMENSIONS}
                                            selectedDimension={CUSTOM_SELECTED_DIMENSION}
                                            selectedPositions={CUSTOM_SELECTED_POSITIONS}
                                            isError={ERROR_DIMENSIONS}
                                            infoTypes={CUSTOM_TYPES}
                                            isLoading={LOADING_DIMENSIONS || CUSTOM_DIMENSIONS === null}
                                            onSelectDimension={(newDimensions, selected, selectedDimension) =>
                                                handleOnSelectDimension({
                                                    newDimensions,
                                                    selected,
                                                    selectedDimension,
                                                    dims: true,
                                                })
                                            }
                                        />
                                    </Grid>

                                    {isMobile ? (
                                        <Grid item xs={12} className={classes.gridItemPositions}>
                                            <Typography
                                                variant={"body1"}
                                                className={classes.posTitle}
                                                sx={{ mt: { sm: 1, md: 0 } }}
                                            >
                                                {t("Pozycje")}
                                                {": "}
                                            </Typography>
                                        </Grid>
                                    ) : null}
                                    <Grid item xs sm md={7} lg={8} xl={7} sx={{ position: "relative" }}>
                                        <Positions
                                            currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                            currentSection={CUSTOM_CURRENT_SECTION}
                                            dimensions={CUSTOM_DIMENSIONS}
                                            selectedDimension={CUSTOM_SELECTED_DIMENSION}
                                            selectedPositions={CUSTOM_SELECTED_POSITIONS}
                                            isError={ERROR_DIMENSIONS}
                                            isLoading={LOADING_DIMENSIONS || CUSTOM_DIMENSIONS === null}
                                            onCheckPosition={(newDimensions, newSelectedPositions) =>
                                                handleOnSelectPosition({ newDimensions, newSelectedPositions })
                                            }
                                            onZoom={() => handleZoom({ elem: "dimensions" })}
                                        />
                                    </Grid>
                                </Grid>

                                {ZOOMED.dimensions ? (
                                    <DimensionsZoomDialog
                                        open={ZOOMED.dimensions}
                                        setOpen={() => handleZoom({ elem: "dimensions" })}
                                    >
                                        <Grid container spacing={2} style={{ height: "100%" }}>
                                            <Grid item xs={12} sm={12} md={4} lg={3}>
                                                <Dimensions
                                                    currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                                    currentSection={CUSTOM_CURRENT_SECTION}
                                                    dimensions={CUSTOM_DIMENSIONS}
                                                    selectedDimension={CUSTOM_SELECTED_DIMENSION}
                                                    selectedPositions={CUSTOM_SELECTED_POSITIONS}
                                                    isError={ERROR_DIMENSIONS}
                                                    infoTypes={CUSTOM_TYPES}
                                                    isLoading={LOADING_DIMENSIONS || CUSTOM_DIMENSIONS === null}
                                                    onSelectDimension={(newDimensions, selected, selectedDimension) =>
                                                        handleOnSelectDimension({
                                                            newDimensions,
                                                            selected,
                                                            selectedDimension,
                                                            dims: true,
                                                        })
                                                    }
                                                    isZoomed={ZOOMED.dimensions}
                                                />
                                            </Grid>
                                            <Grid
                                                item
                                                xs
                                                sm
                                                md={8}
                                                lg={9}
                                                sx={{
                                                    position: "relative",
                                                    display: { sm: "contents", md: "block", lg: "block" },
                                                }}
                                            >
                                                <Positions
                                                    currentIndicator={CUSTOM_CURRENT_INDICATOR}
                                                    currentSection={CUSTOM_CURRENT_SECTION}
                                                    dimensions={CUSTOM_DIMENSIONS}
                                                    selectedDimension={CUSTOM_SELECTED_DIMENSION}
                                                    selectedPositions={CUSTOM_SELECTED_POSITIONS}
                                                    isError={ERROR_DIMENSIONS}
                                                    isLoading={LOADING_DIMENSIONS || CUSTOM_DIMENSIONS === null}
                                                    onCheckPosition={(newDimensions, newSelectedPositions) =>
                                                        handleOnSelectPosition({ newDimensions, newSelectedPositions })
                                                    }
                                                    isZoomed={ZOOMED.dimensions}
                                                />
                                            </Grid>
                                        </Grid>
                                    </DimensionsZoomDialog>
                                ) : null}
                            </Grid>
                        </Grid>
                    </FiltersAccordion>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Filters;

const checkDeep = (node, checked_id, checked, all = false) => {
    const { position_id, children = [] } = node;

    const _hasChildren = !!children?.length;
    return {
        ...node,
        children: _hasChildren ? children?.map((child) => checkDeep(child, checked_id, checked, all)) : [],
        checked: all ? checked : position_id === checked_id ? checked : node.checked,
    };
};
